import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import * as LucideIcons from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { ArrowRight, Tag } from "lucide-react";

// Couleurs d'accent par slug (visuel différencié mais cohérent avec la charte)
const COLORS = {
  "restauration":              { bg: "bg-orange-50",   fg: "text-[#E05638]", border: "hover:border-[#E05638]" },
  "alimentation":              { bg: "bg-amber-50",    fg: "text-amber-700", border: "hover:border-amber-500" },
  "beaute-bien-etre":          { bg: "bg-pink-50",     fg: "text-pink-600",  border: "hover:border-pink-500" },
  "auto-moto":                 { bg: "bg-slate-100",   fg: "text-slate-700", border: "hover:border-slate-500" },
  "loisirs-culture":           { bg: "bg-violet-50",   fg: "text-violet-700", border: "hover:border-violet-500" },
  "sport-fitness":             { bg: "bg-emerald-50",  fg: "text-emerald-700", border: "hover:border-emerald-500" },
  "maison-jardin":             { bg: "bg-lime-50",     fg: "text-lime-700",  border: "hover:border-lime-500" },
  "artisans-services":         { bg: "bg-blue-50",     fg: "text-blue-700",  border: "hover:border-blue-500" },
  "professionnels-entreprises":{ bg: "bg-indigo-50",   fg: "text-indigo-700",border: "hover:border-indigo-500" },
};

function CategoryIcon({ name, className }) {
  const Icon = LucideIcons[name] || Tag;
  return <Icon className={className}/>;
}

export default function Categories() {
  useSeo({
    title: "Catégories de commerces et services locaux en Eure-et-Loir | PROXIreduc-28",
    description: "Parcourez toutes les catégories de commerces, artisans et services de l'Eure-et-Loir : restaurants, beauté, maison, loisirs et plus. Trouvez des bons plans près de chez vous.",
    canonicalPath: "/categories",
    image: "/og-image.jpg",
  });
  const [cats, setCats] = useState([]);
  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data || []));
    document.title = "Toutes les catégories · PROXIreduc-28";
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="categories-page">
      <div className="max-w-2xl">
        <div className="text-xs font-bold uppercase tracking-widest text-emerald-700">Explorer</div>
        <h1 className="mt-1 font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900">
          Toutes les catégories
        </h1>
        <p className="mt-2 text-slate-600">
          9 thèmes, une centaine de sous-catégories. Trouvez précisément le bon plan dont vous avez besoin.
        </p>
      </div>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-5" data-testid="categories-grid">
        {cats.map((c) => {
          const col = COLORS[c.slug] || { bg: "bg-slate-50", fg: "text-slate-700", border: "hover:border-slate-500" };
          const nb = c.offer_count || 0;
          const nbBiz = c.business_count || 0;
          return (
            <Link key={c.id} to={`/categories/${c.slug}`}
                  data-testid={`category-card-${c.slug}`}
                  className={`group card-hover bg-white border border-slate-200 ${col.border} rounded-3xl p-6 flex flex-col transition-all`}>
              <div className={`w-14 h-14 rounded-2xl ${col.bg} ${col.fg} grid place-items-center`}>
                <CategoryIcon name={c.icon} className="w-7 h-7"/>
              </div>
              <div className="mt-4 font-heading font-extrabold text-lg md:text-xl text-slate-900 leading-tight">
                {c.name}
              </div>
              {c.description && (
                <p className="mt-1 text-sm text-slate-500 line-clamp-2">{c.description}</p>
              )}
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`category-card-${c.slug}-stats`}>
                {nb > 0 && (
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#E05638]/10 text-[#E05638]">
                    {nb} bon{nb > 1 ? "s" : ""} plan{nb > 1 ? "s" : ""}
                  </span>
                )}
                {nbBiz > 0 && (
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                    {nbBiz} partenaire{nbBiz > 1 ? "s" : ""}
                  </span>
                )}
                {(c.subcategories?.length || 0) > 0 && (
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                    {c.subcategories.length} sous-catégories
                  </span>
                )}
              </div>
              <div className={`mt-4 inline-flex items-center gap-1 font-semibold text-sm ${col.fg}`}>
                Explorer <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5"/>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
